import * as Yup from 'yup';
import { DESTINATION_TYPES } from '../../constants/destinationTypes';
import {
  DestinationType
} from '../../interfaces/destinationType';
import { MS_SQL_AUTH_METHOD } from './constants';
import {
  getSSHConfigValidationSchema
} from '../../../../connector/connector-form/components/ssh-config/validationSchema';
import { getSSLConfigValidationSchema } from '../../../../connector/connector-form/components/ssl-config/validationSchema';
import { SetupGuideLinkParams } from '../../../../docs/utils/setupGuideLinkHandler';
import {
  hostNameValidatorSchema,
  localHostValidatorSchema,
  schemaNameValidatorSchema
} from '../../../../form/utils/validatorSchema';
import { DestinationConfigMetaData } from '../interface';
import Sql from './Sql';

export const fieldNameMapping = {
  host: 'db_host',
  user: 'db_user',
  port: 'db_port',
  databaseName: 'db_name',
  password: 'db_password',
  sshIp: 'ssh_host',
  sshPort: 'ssh_port',
  sshUser: 'ssh_user',
  schemaName: 'schema_name',
  optimizeTables: 'optimisation',
  useSSH: 'use_ssh',
  useSSL: 'use_ssl',
  sanitizeName: 'sanitize_name',
  authenticationType: 'entra_auth_type',
  clientId: 'entra_client_id',
  clientSecret: 'entra_client_secret'
};

export const SQL_CONFIG: DestinationConfigMetaData = {
  formComponent: Sql,
  initialValues: {
    host: '',
    port: '',
    user: '',
    password: '',
    databaseName: '',
    sqlSchema: '',
    useSSL: false,
    useSSH: false,
    sshIp: '',
    sshPort: '',
    sshUser: '',
    sslCA: null,
    sslClientCert: null,
    sslClientKey: null,
    sanitizeName: true,
    sslLoadAllCA: false,
    authenticationType: MS_SQL_AUTH_METHOD.DATABASE_HOST,
    clientId: '',
    clientSecret: ''
  },
  validationSchema(props: { destinationTypeMetaData: DestinationType }) {
    const destinationTypeIdentifier = props.destinationTypeMetaData.name;

    const isMsSql = destinationTypeIdentifier === 'MS_SQL' || destinationTypeIdentifier === 'AWS_RDS_MSSQL';
    const isEntraId = (val: any) => val === MS_SQL_AUTH_METHOD.ENTRA_ID;

    let sqlSchema: { [key: string]: any } = {
      host: isMsSql
        ? Yup.string().trim().when('authenticationType', {
            is: isEntraId,
            then: schema => schema.required(),
            otherwise: schema => schema
              .test(localHostValidatorSchema({
                type: 'SQL',
                typeIdentifier: destinationTypeIdentifier,
                localhostDocLinkParams: getLocalhostSBSLinkParams(destinationTypeIdentifier)
              }))
              .test(hostNameValidatorSchema({ type: 'SQL' }))
              .required()
          })
        : Yup.string()
            .trim()
            .test(localHostValidatorSchema({
              type: 'SQL',
              typeIdentifier: destinationTypeIdentifier,
              localhostDocLinkParams: getLocalhostSBSLinkParams(destinationTypeIdentifier)
            }))
            .test(hostNameValidatorSchema({ type: 'SQL' }))
            .required(),
      port: isMsSql
        ? Yup.number().positive().when('authenticationType', {
            is: isEntraId,
            then: schema => schema.required(),
            otherwise: schema => schema.required()
          })
        : Yup.number().positive().required(),
      user: isMsSql
        ? Yup.string().trim().when('authenticationType', {
            is: isEntraId,
            then: schema => schema.notRequired(),
            otherwise: schema => schema.required()
          })
        : Yup.string().trim().required(),
      password: isMsSql
        ? Yup.string().trim().when('authenticationType', {
            is: isEntraId,
            then: schema => schema.notRequired(),
            otherwise: schema => schema.required()
          })
        : Yup.string().trim().required(),
      databaseName: isMsSql
        ? Yup.string().trim().when('authenticationType', {
            is: isEntraId,
            then: schema => schema.required(),
            otherwise: schema => schema.required()
          })
        : Yup.string().trim().required(),
      useSSL: Yup.boolean(),
      ...getSSHConfigValidationSchema()
    };
    if (props.destinationTypeMetaData.canUseSSL) {
      sqlSchema = {
        ...sqlSchema,
        ...getSSLConfigValidationSchema(props.destinationTypeMetaData.sslCARequired)
      };
    }

    if (props.destinationTypeMetaData.provideSchemaName) {
      sqlSchema.schemaName = Yup.string().trim().test(schemaNameValidatorSchema());
    }

    if (isMsSql) {
      sqlSchema.authenticationType = Yup.string().required();
      sqlSchema.clientId = Yup.string().trim().when('authenticationType', {
        is: isEntraId,
        then: schema => schema.required(),
        otherwise: schema => schema.notRequired()
      });
      sqlSchema.clientSecret = Yup.string().trim().when('authenticationType', {
        is: isEntraId,
        then: schema => schema.required(),
        otherwise: schema => schema.notRequired()
      });
    }

    return Yup.object().shape(
      { ...sqlSchema },

      [['sslClientCert', 'sslClientKey']]
    );
  },
  fieldNameMapping
};
export function getLocalhostSBSLinkParams(destinationTypeIdentifier) {
  const params: SetupGuideLinkParams = {
    setupGuideLink: `${DESTINATION_TYPES[destinationTypeIdentifier].docLink}#connect-to-a-local-database`,
    section: 'destination_settings',
    label: 'db-host-field-local-host-steps',
    properties: {
      destinationType: destinationTypeIdentifier,
      context: 'Destination'
    }
  };
  return params;
}
